/**
 * Theme Provider
 *
 * Manages application theme (light/dark/system) by:
 * - Reading theme preference from Zustand UI store
 * - Detecting system color scheme preference
 * - Applying 'dark' class to <html> element for Tailwind dark mode
 */

import { useUIStore } from '@medsuite/state-management';
import { useEffect } from 'react';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useUIStore((state) => state.theme);

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Determine effective theme
    let effectiveTheme: 'light' | 'dark' = 'light';

    if (theme === 'system') {
      // Detect system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      effectiveTheme = systemPrefersDark ? 'dark' : 'light';
    } else {
      effectiveTheme = theme;
    }

    // Apply theme class
    if (effectiveTheme === 'dark') {
      root.classList.add('dark');
    }
  }, [theme]);

  // Listen for system theme changes when theme='system'
  useEffect(() => {
    if (theme !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');

      if (e.matches) {
        root.classList.add('dark');
      }
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  return <>{children}</>;
}
